import { useRef, useMemo } from "react"
import { motion, useInView } from "framer-motion"
import styled from "@emotion/styled"

import { slideUp } from "animations/slideUp"

import { useConsole } from "contexts/Console"

import getMediaQuery from "css/breakpoints"
import { fullGrid } from "css/grid"

import generateModularBlocks from "utils/generateModularBlocks"

import Video from "components/media/VideoCLD"
import FigCaption from "components/figcaption/FigCaption"

import { getGradient, modularTexts } from "./common"

const Root = styled.article`
  ${fullGrid}
  align-items: center;
`

const VideoFigure = styled.figure`
  grid-column: col 1 / span 6;
  grid-row: 1;
  position: relative;
  padding-block-end: 40px;
  display: grid;

  --transition: 0.9s cubic-bezier(0.17, 0.55, 0.55, 1) 0.1s;
  clip-path: ${({ isInView, animate }) => animate && (isInView ? "inset(0%)" : "inset(10%)")};
  transition: clip-path var(--transition);
  will-change: clip-path;

  & picture {
    display: flex;
  }

  & figcaption {
    width: 66%;
  }

  ${getMediaQuery("m")} {
    grid-column: col 7 / span 6;
    grid-row: 1;

    padding-block: 0;

    .s-w-video & {
      padding-block: 0;
      grid-column: col 1 / span 6;
    }
  }

  & video {
    width: 100%;
  }
`

const Container = styled(motion.div)`
  grid-column: main;
  grid-row: article;
  grid-row: 2;

  ${getMediaQuery("m")} {
    grid-row: 1;
    margin-block-end: 0;
    grid-column: col 1 / span 5;
    .s-w-video & {
      grid-column: col 8 / span 5;
    }
  }

  & figure:not(:last-child) {
    padding-block-end: 30px;
  }

  ${modularTexts}

  hgroup {
    margin-block-end: 20px;
  }

  hgroup,
  h3,
  p {
    html.prefers-contrast & {
      color: black !important;
      position: relative;

      ::after {
        background: white;
        content: "";
        height: calc(100% + 30px);
        left: 50%;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        width: calc(100% + 20px);
        z-index: -1;
      }
    }
  }
`

export default function VideoTextHorizontal({
  items,
  background,
  video_cld: video,
  start_with_video,
  className,
  title_gradient,
  chapo_gradient,
  noButton,
  animation,
  fig_caption,
  ...props
}) {
  const console = useConsole()

  const modularBlocks = useMemo(() => generateModularBlocks(items, { console }), [])
  const prefersReducedMotion = process.browser && document.documentElement.classList.contains("prefers-reduced-motion")
  const bgcol = background?.colors.map(({ color, opacity }) => `rgb(${color} / ${opacity})`).join(",")

  const ttlGradient = title_gradient?.colors ? getGradient(title_gradient?.colors) : null
  const chapoGradient = chapo_gradient?.colors ? getGradient(chapo_gradient?.colors) : null

  const animate = animation === "mask"

  const videoRef = useRef()
  const rvideo = useRef()
  const textContRef = useRef()
  const videoIsInView = useInView(videoRef, { margin: "100% 0% 0% 0%" })
  const isInView = useInView(textContRef, { margin: "100% 0% 0% 0%" })

  return (
    <Root
      className={`${className}${start_with_video ? " s-w-video" : ""}`}
      style={{
        background: background?.colors.length > 1 ? `linear-gradient(${bgcol})` : bgcol,
        "--title-gradient": ttlGradient,
        "--chapo-gradient": chapoGradient,
      }}
    >
      <VideoFigure className='dark-theme' ref={videoRef} isInView={videoIsInView} animate={animate}>
        {video && <Video ref={rvideo} noButton={noButton} autoPlay {...video} sizes='(min-width: 48rem) 42vw, 85vw' />}
        {fig_caption?.caption?.length ? <FigCaption {...fig_caption} /> : null}
      </VideoFigure>
      <Container ref={textContRef} variants={slideUp} initial='hidden' animate={isInView ? "show" : "hide"}>
        {modularBlocks}
      </Container>
    </Root>
  )
}
